import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Feather, GoogleLogo, MicrophoneStage, CloudArrowUp, Sparkle } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";

export default function Landing() {
  const navigate = useNavigate();

  useEffect(() => {
    // If already logged in, hop to dashboard
    if (window.location.hash?.includes("session_id=")) return;
    api.get("/auth/me").then(() => navigate("/dashboard")).catch(() => {});
  }, [navigate]);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen paper-grain" data-testid="landing-page">
      {/* Nav */}
      <nav className="sticky top-0 z-30 backdrop-blur-md bg-background/80 border-b border-border">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2">
            <Feather size={24} weight="duotone" className="text-primary" />
            <span className="font-serif text-2xl tracking-tight">Antic</span>
          </div>
          <Button
            data-testid="nav-login-btn"
            onClick={handleLogin}
            className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 px-6"
          >
            <GoogleLogo size={18} weight="bold" className="mr-2" />
            Sign in
          </Button>
        </div>
      </nav>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-16 pb-24 lg:pt-28 lg:pb-32 grid lg:grid-cols-2 gap-16 items-center">
        <div className="fade-up">
          <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-6">
            A daily diary · Est. 2026
          </p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter leading-none text-foreground">
            The days you<br/>
            <span className="italic text-accent">forget to write</span><br/>
            are the ones you lose.
          </h1>
          <p className="mt-8 text-base leading-relaxed text-muted-foreground max-w-md">
            A quiet, private diary for the small antics of daily life. Write with your voice.
            Keep photos, videos, audio and papers all in one place. 1&nbsp;GB free — or route
            everything to your own Google Drive.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Button
              data-testid="hero-get-started-btn"
              onClick={handleLogin}
              size="lg"
              className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 px-8 h-12"
            >
              <GoogleLogo size={18} weight="bold" className="mr-2" />
              Begin your diary
            </Button>
            <a
              href="#features"
              data-testid="hero-features-link"
              className="text-sm uppercase tracking-[0.2em] font-semibold self-center hover:text-accent transition-colors"
            >
              What's inside →
            </a>
          </div>
        </div>
        <div className="relative">
          <img
            src="https://images.unsplash.com/photo-1592271019141-b5c71a9cfd71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHx2aW50YWdlJTIwam91cm5hbCUyMGFlc3RoZXRpY3xlbnwwfHx8fDE3ODU3OTY4MDB8MA&ixlib=rb-4.1.0&q=85"
            alt="A quiet journal"
            className="w-full h-[520px] object-cover rounded-lg border border-border shadow-md"
          />
          <div className="absolute -bottom-6 -left-6 bg-card border border-border rounded-lg p-4 shadow-md hidden sm:block">
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Today</p>
            <p className="font-serif text-4xl mt-1">Wed, 12</p>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="border-t border-border">
        <div className="max-w-6xl mx-auto px-6 py-20 grid md:grid-cols-3 gap-10">
          {[
            { Icon: MicrophoneStage, title: "Speak, don't type",
              desc: "Ramble into your phone. Whisper transcribes it into readable text, ready to keep or edit." },
            { Icon: CloudArrowUp, title: "Every kind of memory",
              desc: "Attach videos, audio, photos, PDFs — up to 1 GB free. Connect Google Drive for unlimited." },
            { Icon: Sparkle, title: "Grow your archive",
              desc: "Buy more storage at just ₹100 per GB per month. Cancel anytime." },
          ].map(({ Icon, title, desc }, i) => (
            <div key={i} className="fade-up" style={{ animationDelay: `${i * 0.1}s` }}>
              <Icon size={32} weight="duotone" className="text-accent mb-6" />
              <h3 className="font-serif text-2xl mb-2">{title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-6xl mx-auto px-6 py-8 flex items-center justify-between">
          <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
            Antic · 2026
          </span>
          <span className="text-xs text-muted-foreground">
            Private by design.
          </span>
        </div>
      </footer>
    </div>
  );
}
